/**
 * A valence tab of the drill-in panel when it is opened from a product (PLT-7045).
 *
 * A product has no contributing products to rank, so where a company's tab lists them this one
 * holds what sits under one product's category score: its Leena-2 impact topics, for a team
 * reader looking at Leena-2 product data, and otherwise the way into the full product
 * drilldown (score composition, value chains, the research behind the score).
 */
import { Valence } from '@upright/common/model/types';
import { Link } from '@upright/ui/common/generic/components/link';
import { Stack } from '@upright/ui/common/generic/components/layout';
import { Text } from '@upright/ui/common/generic/components/typography';
import { useIsTeam } from '@upright/ui/common/generic/hooks';
import { useIsLeena2ResearchPreview } from '@upright/ui/common/upright/model/output/leena2-research-preview';
import { getModelEntityDrilldownUrl } from '@upright/ui/untidy/urls';
import { Leena2TopImpacts } from '@upright/ui/views/model-entity-views/company-view/drilldown/leena2-top-impacts';
import { DrillInSectionHeader } from '../components/section-header';
import { NetImpactDrillInPayload } from '../state';
import { formatHeaderCentsPerDollar } from './contributors';

interface Props {
  payload: NetImpactDrillInPayload;
  valence: Valence.POSITIVE | Valence.NEGATIVE;
  impactLabel: string;
  entityName: string;
}

export function ProductValenceTab({ payload, valence, impactLabel, entityName }: Props) {
  const isTeam = useIsTeam();
  // Leena-2 topics are stored for the Leena-2 product model only, so they are shown when that is
  // the version on screen; against any other version they would explain a different score.
  const isLeena2 = useIsLeena2ResearchPreview();
  const valenceWord = valence === Valence.POSITIVE ? 'positive' : 'negative';
  const centsPerDollar = valence === Valence.POSITIVE ? payload.positiveCentsPerDollar : payload.negativeCentsPerDollar;
  const impactCode = payload.target.grain === 'impact' ? payload.target.impactCode : undefined;
  // Not offered on the Leena-2 version, where the panel's own topic list below is what explains
  // the score (PLT-7063).
  const drilldownHref =
    !isLeena2 && impactCode && payload.modelResultId !== undefined
      ? getModelEntityDrilldownUrl({
          type: payload.entity.type,
          id: payload.entity.id,
          name: payload.entity.name,
          impactCode,
          valence,
          resultId: payload.modelResultId,
        })
      : undefined;

  return (
    <Stack gap="2xl">
      <Stack gap="md">
        <DrillInSectionHeader
          title={`${entityName}'s ${valenceWord} ${impactLabel.toLowerCase()} impact`}
          figure={formatHeaderCentsPerDollar(centsPerDollar, valence)}
          figureValence={valence}
        />
        {drilldownHref && (
          <Stack gap="sm">
            <Text size="sm" color="gray-700">
              The full drilldown shows how the score is composed, how it splits across the value chain, and the research
              behind it.
            </Text>
            {/* Its own line at medium weight, like the contributors tab's "View all" link. */}
            <Text size="sm" weight="medium">
              <Link href={drilldownHref} decoration="all">
                Open the full drilldown →
              </Link>
            </Text>
          </Stack>
        )}
      </Stack>
      {isTeam && isLeena2 && impactCode && (
        <Leena2TopImpacts
          source={{ kind: 'product', productCode: payload.entity.id }}
          impactCode={impactCode}
          valence={valence}
          showValueChains={payload.showValueChains}
        />
      )}
    </Stack>
  );
}
